import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Pagination, Spin, Tag, message } from "antd";
import {
  ApiOutlined,
  CloudUploadOutlined,
  CodeOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import intl from "react-intl-universal";
import { skillList } from "@/apis/skillhub";
import styles from "./index.module.scss";

const getText = (key) => intl.get(`SkillHub.${key}`);

const splitTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.filter(Boolean).map((item) => String(item));
  return String(tags)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeListPayload = (payload) => {
  if (Array.isArray(payload)) {
    return {
      records: payload,
      total: payload.length,
      isArrayPayload: true,
    };
  }

  const records = payload?.records || payload?.list || payload?.items || payload?.content || [];
  return {
    records,
    total: payload?.total || payload?.totalElements || records.length,
    isArrayPayload: false,
  };
};

const getSkillTitle = (item) => item.title || item.name || item.skillName || item.skillUuid || "-";
const getSkillAuthor = (item) => item.author || item.ownerName || item.owner || item.creatorName;
const renderText = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

const SkillHub = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState({
    keywords: "",
    onlyMine: false,
  });
  const [page, setPage] = useState({
    currentPage: 1,
    pageSize: 9,
  });

  const visibleList = useMemo(() => list, [list]);

  const statusMap = {
    0: { text: getText("Draft"), color: "default" },
    1: { text: getText("Published"), color: "green" },
    2: { text: getText("Disabled"), color: "red" },
  };

  const auditStatusMap = {
    0: { text: getText("Not submitted"), color: "default" },
    1: { text: getText("Pending Review"), color: "processing" },
    2: { text: getText("Approved"), color: "green" },
    3: { text: getText("Rejected"), color: "red" },
  };

  const fetchList = useCallback(() => {
    setLoading(true);
    skillList({
      keywords: query.keywords,
      ownerUuid: query.onlyMine ? userInfo?.uuid : undefined,
      currentPage: page.currentPage,
      pageSize: page.pageSize,
    })
      .then((res) => {
        const data = normalizeListPayload(res.data || {});
        const offset = (page.currentPage - 1) * page.pageSize;
        setList(data.isArrayPayload ? data.records.slice(offset, offset + page.pageSize) : data.records);
        setTotal(data.total);
      })
      .catch(() => {
        message.error(getText("Failed to load skills"));
      })
      .finally(() => setLoading(false));
  }, [page.currentPage, page.pageSize, query.keywords, query.onlyMine, userInfo?.uuid]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const search = () => {
    if (page.currentPage === 1) {
      fetchList();
    } else {
      setPage({ ...page, currentPage: 1 });
    }
  };

  const goDetail = (skillUuid) => {
    navigate(`/skillhub/detail?id=${skillUuid}`);
    window.scrollTo(0, 0);
  };

  return (
    <div className={styles.skillHubPage}>
      <section className={styles.hero}>
        <div className={classNames("container", styles.heroInner)}>
          <div>
            <h1>{getText("Open Skill Hub")}</h1>
            <p className={styles.heroText}>
              {getText("Hero Description")}
            </p>
          </div>
        </div>
      </section>

      <main className={classNames("container", styles.content)}>
        <div className={styles.toolbar}>
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            placeholder={getText("Search Placeholder")}
            value={query.keywords}
            onChange={(e) => setQuery({ ...query, keywords: e.target.value })}
            onPressEnter={search}
          />
          <Button type="primary" size="large" icon={<SearchOutlined />} onClick={search}>
            {intl.get("Search")}
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<CloudUploadOutlined />}
            onClick={() => navigate("/skillhub/upload")}
          >
            {getText("Publish Skill")}
          </Button>
          {Number(userInfo?.roleId) === 1 ? (
            <Button size="large" onClick={() => navigate("/administration/skillhub-auditing")}>
              {getText("Review Queue")}
            </Button>
          ) : null}
        </div>

        <div className={styles.summaryRow}>
          <div>
            <ThunderboltOutlined />
            <span>{getText("Executable skill packages")}</span>
          </div>
          <div>
            <SafetyCertificateOutlined />
            <span>{getText("Versioned registry records")}</span>
          </div>
          <div>
            <ApiOutlined />
            <span>{getText("Agent invoke specifications")}</span>
          </div>
        </div>

        <Spin spinning={loading}>
          {visibleList.length ? (
            <div className={styles.grid}>
              {visibleList.map((item) => {
                const status = statusMap[item.status] || statusMap[0];
                const auditStatus = auditStatusMap[item.auditStatus] || auditStatusMap[0];
                const title = getSkillTitle(item);
                const author = getSkillAuthor(item);
                return (
                  <article
                    className={styles.card}
                    key={item.skillUuid}
                    onClick={() => goDetail(item.skillUuid)}
                  >
                    <div className={styles.cardTop}>
                      <div className={styles.iconBox}>
                        <CodeOutlined />
                      </div>
                      <div>
                        {item.status !== undefined ? <Tag color={status.color}>{status.text}</Tag> : null}
                        {item.auditStatus !== undefined ? <Tag color={auditStatus.color}>{auditStatus.text}</Tag> : null}
                        {item.fileUuid ? <Tag color="blue">skill.md</Tag> : null}
                      </div>
                    </div>
                    <h3 title={title}>{title}</h3>
                    <p className={styles.description}>{item.description || getText("No description")}</p>
                    <div className={styles.meta}>
                      <span>{item.author ? getText("Author") : getText("Runtime")}: {renderText(author || item.runtime)}</span>
                      <span>v{renderText(item.version)}</span>
                    </div>
                    <div className={styles.tags}>
                      {splitTags(item.tags).slice(0, 4).map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </div>
                    <div className={styles.owner}>
                      {item.fileUuid ? `${getText("File UUID")}: ${item.fileUuid}` : getText("OGA contributor")}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={styles.empty}>
              <Empty />
            </div>
          )}
        </Spin>

        <Pagination
          align="end"
          current={page.currentPage}
          pageSize={page.pageSize}
          total={total}
          showSizeChanger={false}
          onChange={(currentPage, pageSize) => setPage({ currentPage, pageSize })}
        />
      </main>
    </div>
  );
};

export default SkillHub;
